import { useState } from 'react';
import {
  GRID,
  INK_MUTED,
  Legend,
  PREVIOUS_INK,
  SURFACE,
  TooltipBox,
  compactNumber,
  niceMax,
  topRoundedBar,
  useElementWidth
} from './common';

export interface BarSeries {
  key: string;
  label: string;
  color: string;
  values: number[];
  note?: string;
}

/**
 * Colunas empilhadas por origem (uma série = colunas simples, sem legenda).
 * Coluna ≤ 24px, topo arredondado 4px, base reta; 2px de superfície entre
 * segmentos. Tooltip por coluna (todas as séries do X), por hover, toque ou
 * foco de teclado — e a tabela da aba carrega os mesmos números sem hover.
 *
 * `previous` (opcional): total do período anterior na mesma posição, como
 * linha tracejada de tinta por cima das colunas — mesma unidade, mesmo eixo
 * (nunca eixo duplo). Entra na legenda e no tooltip.
 */
export function StackedBars({
  labels,
  series,
  formatLabel,
  formatValue,
  height = 200,
  ariaLabel,
  previous
}: {
  labels: string[];
  series: BarSeries[];
  formatLabel: (l: string) => string;
  formatValue: (v: number) => string;
  height?: number;
  ariaLabel: string;
  previous?: { label: string; values: number[] };
}) {
  const [ref, width] = useElementWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const n = labels.length;
  if (n === 0 || series.length === 0) {
    return <div className="py-10 text-center text-[13px] text-bow-royal/45">Sem recargas no período.</div>;
  }

  const padL = 36;
  const padR = 4;
  const padT = 8;
  const padB = 22;
  const plotW = Math.max(width - padL - padR, 10);
  const plotH = height - padT - padB;
  const totals = labels.map((_, i) => series.reduce((acc, s) => acc + (s.values[i] ?? 0), 0));
  const prev = previous ? labels.map((_, i) => previous.values[i] ?? 0) : null;
  const vmax = niceMax(Math.max(...totals, ...(prev ?? [])));
  const step = plotW / n;
  const bw = Math.max(Math.min(step - 4, 24), 2);
  const labelEvery = Math.max(1, Math.ceil(n / Math.max(Math.floor(plotW / 56), 1)));
  const y = (v: number) => padT + plotH * (1 - v / vmax);

  return (
    <div ref={ref} className="relative">
      {hover !== null && (
        <TooltipBox
          title={formatLabel(labels[hover]!)}
          width={width}
          x={padL + hover * step + step / 2}
          rows={[
            ...series
              .filter((s) => (s.values[hover] ?? 0) > 0)
              .map((s) => ({ label: s.label, value: formatValue(s.values[hover] ?? 0), color: s.color })),
            ...(series.length > 1 ? [{ label: 'Total', value: formatValue(totals[hover] ?? 0) }] : []),
            ...(prev && previous
              ? [{ label: previous.label, value: formatValue(prev[hover] ?? 0), color: PREVIOUS_INK }]
              : [])
          ]}
        />
      )}
      <svg
        width={width}
        height={height}
        role="img"
        aria-label={ariaLabel}
        style={{ display: 'block', overflow: 'visible' }}
        onPointerLeave={() => setHover(null)}
      >
        {[0, 0.5, 1].map((f) => (
          <g key={f}>
            <line x1={padL} x2={width - padR} y1={y(vmax * f)} y2={y(vmax * f)} stroke={GRID} strokeWidth={1} />
            <text x={padL - 6} y={y(vmax * f) + 3} textAnchor="end" fontSize={10} fill={INK_MUTED}>
              {compactNumber(vmax * f)}
            </text>
          </g>
        ))}
        {labels.map((l, i) => {
          const x = padL + i * step + (step - bw) / 2;
          let base = padT + plotH;
          const segs = series
            .map((s) => ({ s, v: s.values[i] ?? 0 }))
            .filter((p) => p.v > 0);
          return (
            <g
              key={l}
              tabIndex={0}
              role="button"
              aria-label={`${formatLabel(l)}: ${formatValue(totals[i] ?? 0)}`}
              onPointerEnter={() => setHover(i)}
              onPointerDown={() => setHover(i)}
              onFocus={() => setHover(i)}
              onBlur={() => setHover(null)}
              style={{ outline: 'none', cursor: 'default' }}
            >
              <rect x={padL + i * step} y={padT} width={step} height={plotH} fill="transparent" />
              {segs.map((p, k) => {
                const h = (plotH * p.v) / vmax;
                const top = k === segs.length - 1;
                const yTop = base - h;
                // 2px de superfície entre segmentos (o de baixo encosta no eixo).
                const gap = k > 0 ? 2 : 0;
                const el = top ? (
                  <path
                    key={p.s.key}
                    d={topRoundedBar(x, yTop, bw, Math.max(h - gap, 0.5))}
                    fill={p.s.color}
                    opacity={hover === null || hover === i ? 1 : 0.55}
                  />
                ) : (
                  <rect
                    key={p.s.key}
                    x={x}
                    y={yTop}
                    width={bw}
                    height={Math.max(h - gap, 0.5)}
                    fill={p.s.color}
                    opacity={hover === null || hover === i ? 1 : 0.55}
                  />
                );
                base = yTop;
                return el;
              })}
              {hover === i && (
                <rect x={x - 2} y={padT} width={bw + 4} height={plotH} fill="none" stroke={SURFACE} />
              )}
            </g>
          );
        })}
        {prev && (
          <g pointerEvents="none">
            {n === 1 ? (
              <line
                x1={padL + step / 2 - 14}
                x2={padL + step / 2 + 14}
                y1={y(prev[0] ?? 0)}
                y2={y(prev[0] ?? 0)}
                stroke={PREVIOUS_INK}
                strokeWidth={2}
                strokeDasharray="4 3"
              />
            ) : (
              <polyline
                points={prev.map((v, i) => `${padL + i * step + step / 2},${y(v)}`).join(' ')}
                fill="none"
                stroke={PREVIOUS_INK}
                strokeWidth={2}
                strokeDasharray="4 3"
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            )}
            {hover !== null && (
              <circle
                cx={padL + hover * step + step / 2}
                cy={y(prev[hover] ?? 0)}
                r={4}
                fill={PREVIOUS_INK}
                stroke={SURFACE}
                strokeWidth={2}
              />
            )}
          </g>
        )}
        {labels.map((l, i) =>
          i % labelEvery === 0 ? (
            <text
              key={`t-${l}`}
              x={padL + i * step + step / 2}
              y={height - 6}
              textAnchor="middle"
              fontSize={10}
              fill={INK_MUTED}
            >
              {formatLabel(l)}
            </text>
          ) : null
        )}
      </svg>
      <Legend
        items={[
          ...series.map((s) => ({ label: s.label, color: s.color, note: s.note })),
          ...(previous ? [{ label: previous.label, color: PREVIOUS_INK, kind: 'dash' as const }] : [])
        ]}
      />
    </div>
  );
}
